/**
 * Color Pallet Navigation
 * Handles color swatch clicks with section rendering (no full page reload)
 * Mimics Shopify's variant selection behavior
 */

// Global popstate handler - attached once regardless of color palette presence
if (!window.colorPalletPopStateAttached) {
  window.colorPalletPopStateAttached = true;
  
  window.addEventListener('popstate', (event) => {
    // Check if we're on a product page
    const productInfo = document.querySelector('product-info[data-section]');
    if (!productInfo) return;
    
    // Only handle if we have a colorPalletNavigation flag OR if there's any state
    // This ensures we handle back/forward for color palette navigations
    if (!event.state || !event.state.colorPalletNavigation) {
      // Check if we've done any color palette navigation in this session
      if (!window.hasColorPalletNavigated) return;
    }
    
    const currentUrl = window.location.pathname + window.location.search;
    const sectionId = productInfo.dataset.section;
    const separator = currentUrl.includes('?') ? '&' : '?';
    const requestUrl = `${currentUrl}${separator}section_id=${sectionId}`;
    
    fetch(requestUrl)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const newProductInfo = html.querySelector('product-info');
        
        if (newProductInfo) {
          // Update data attributes
          Array.from(newProductInfo.attributes).forEach(attr => {
            if (attr.name.startsWith('data-')) {
              productInfo.setAttribute(attr.name, attr.value);
            }
          });
          
          // Update content
          if (window.HTMLUpdateUtility && window.HTMLUpdateUtility.viewTransition) {
            HTMLUpdateUtility.viewTransition(productInfo, newProductInfo);
          } else {
            productInfo.innerHTML = newProductInfo.innerHTML;
          }
          
          // Update page title
          const newTitle = html.querySelector('head title');
          if (newTitle) {
            document.title = newTitle.textContent;
          }
          
          // Update product modal if exists
          const sectionId = productInfo.dataset.section;
          const currentModal = document.querySelector(`#ProductModal-${sectionId}`);
          const newModal = html.querySelector(`#ProductModal-${sectionId}`);
          
          if (currentModal && newModal) {
            if (window.HTMLUpdateUtility && window.HTMLUpdateUtility.viewTransition) {
              HTMLUpdateUtility.viewTransition(currentModal, newModal);
            } else {
              currentModal.innerHTML = newModal.innerHTML;
            }
          }
        }
      })
      .catch((error) => {
        // Fallback to normal navigation on error
        window.location.reload();
      });
  });
}

if (!customElements.get('color-pallet-navigation')) {
  customElements.define(
    'color-pallet-navigation',
    class ColorPalletNavigation extends HTMLElement {
      constructor() {
        super();
        this.abortController = null;
      }

      connectedCallback() {
        this.attachSwatchHandlers();
      }

      disconnectedCallback() {
        // No need to remove global popstate handler
      }

      attachSwatchHandlers() {
        // Use event delegation for better performance
        this.addEventListener('click', (e) => {
          const swatchLink = e.target.closest('.color-pallet__swatch');
          if (!swatchLink) return;

          // Don't intercept if it's the current product
          if (swatchLink.classList.contains('color-pallet__swatch--selected')) {
            e.preventDefault();
            return;
          }

          e.preventDefault();
          this.navigateToProduct(swatchLink.href);
        });
      }

      navigateToProduct(productUrl) {
        // Cancel any pending requests
        this.abortController?.abort();
        this.abortController = new AbortController();

        // Get the section ID from the main product section
        const productInfo = document.querySelector('product-info[data-section]');
        if (!productInfo) {
          // Fallback to normal navigation if product-info not found
          window.location.href = productUrl;
          return;
        }

        const sectionId = productInfo.dataset.section;
        const requestUrl = `${productUrl}?section_id=${sectionId}`;

        // Show loading state
        this.setLoadingState(true);

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            
            // Update the product-info section
            this.updateProductSection(html, productInfo);
            
            // Update the product modal
            this.updateProductModal(html, sectionId);
            
            // Update URL and page title
            this.updateURL(productUrl, html);
            
            // Scroll to top of product info
            this.scrollToProduct(productInfo);
            
            // Remove loading state
            this.setLoadingState(false);
          })
          .catch((error) => {
            if (error.name !== 'AbortError') {
              // Fallback to normal navigation on error
              window.location.href = productUrl;
            }
          });
      }

      updateProductSection(html, productInfo) {
        const newProductInfo = html.querySelector('product-info');
        if (!newProductInfo) return;

        // CRITICAL: Update data attributes before swapping content
        // This ensures variant selectors use the correct product URL
        productInfo.setAttribute('data-url', newProductInfo.getAttribute('data-url'));
        productInfo.setAttribute('data-product-id', newProductInfo.getAttribute('data-product-id'));
        
        // Copy all data attributes from new to current
        Array.from(newProductInfo.attributes).forEach(attr => {
          if (attr.name.startsWith('data-')) {
            productInfo.setAttribute(attr.name, attr.value);
          }
        });

        // Use Shopify's HTMLUpdateUtility if available, otherwise use innerHTML
        if (window.HTMLUpdateUtility && window.HTMLUpdateUtility.viewTransition) {
          HTMLUpdateUtility.viewTransition(productInfo, newProductInfo);
        } else {
          productInfo.innerHTML = newProductInfo.innerHTML;
          
          // Re-initialize any scripts that need it
          this.reinitializeScripts(productInfo);
        }
      }

      reinitializeScripts(container) {
        // Trigger custom event for other scripts to reinitialize
        container.dispatchEvent(new CustomEvent('product-info:updated', { bubbles: true }));
        
        // Re-run any script tags in the new content
        const scripts = container.querySelectorAll('script');
        scripts.forEach(oldScript => {
          const newScript = document.createElement('script');
          Array.from(oldScript.attributes).forEach(attr => {
            newScript.setAttribute(attr.name, attr.value);
          });
          newScript.textContent = oldScript.textContent;
          oldScript.parentNode.replaceChild(newScript, oldScript);
        });
      }

      updateProductModal(html, sectionId) {
        // Update the product modal with new media
        const currentModal = document.querySelector(`#ProductModal-${sectionId}`);
        const newModal = html.querySelector(`#ProductModal-${sectionId}`);
        
        if (currentModal && newModal) {
          // Use Shopify's HTMLUpdateUtility if available
          if (window.HTMLUpdateUtility && window.HTMLUpdateUtility.viewTransition) {
            HTMLUpdateUtility.viewTransition(currentModal, newModal);
          } else {
            currentModal.innerHTML = newModal.innerHTML;
          }
        }
      }

      updateURL(productUrl, html) {
        // Update page title
        const newTitle = html.querySelector('head title');
        if (newTitle) {
          document.title = newTitle.textContent;
        }

        // Mark that we've done color palette navigation in this session
        window.hasColorPalletNavigated = true;

        // Update URL in browser with flag for popstate handler
        window.history.pushState({ 
          path: productUrl, 
          colorPalletNavigation: true 
        }, '', productUrl);

        // Update share button URL if it exists
        const shareButton = document.querySelector('share-button');
        if (shareButton && shareButton.updateUrl) {
          shareButton.updateUrl(`${window.shopUrl}${productUrl}`);
        }
      }

      scrollToProduct(productInfo) {
        // Smooth scroll to top of product section
        const offset = 100; // Account for sticky header
        const elementPosition = productInfo.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }

      setLoadingState(isLoading) {
        const productInfo = document.querySelector('product-info');
        if (!productInfo) return;

        if (isLoading) {
          productInfo.classList.add('loading');
          productInfo.style.opacity = '0.6';
          productInfo.style.pointerEvents = 'none';
        } else {
          productInfo.classList.remove('loading');
          productInfo.style.opacity = '1';
          productInfo.style.pointerEvents = 'auto';
        }
      }
    }
  );
}
